import * as Sentry from '@sentry/browser'

import getPageContext from 'airship/modules/page-context'

import {rootDir} from '../../../'

let sentryData: null | SentryData = null

interface SentryData {
  SEND_DEFAULT_PII: boolean
  DSN: string
  SENTRY_ENVIRONMENT_TAG: string
  TRACES_SAMPLE_RATE: number
  RELEASE: string
  TAGS: {admin: boolean}
  USER?: {id: number; email: string; username: string}
}

export interface PageContext {
  context_processors: {
    sentry_data: SentryData
  }
}

try {
  const context = getPageContext<PageContext>()
  sentryData = context.context_processors.sentry_data
} catch {
  // no sentry_data present, just leave sentryData null
}

if (sentryData) {
  const dsn = sentryData.DSN
  const environment = sentryData.SENTRY_ENVIRONMENT_TAG
  const release = sentryData.RELEASE
  const tracesSampleRate = sentryData.TRACES_SAMPLE_RATE
  const sendDefaultPii = sentryData.SEND_DEFAULT_PII
  const allowUrls = [
    /.*?\.urbanairship\.com/i,
    /storage\.googleapis\.com\/ua-airship-assets\/.*/i,
    /s3\.amazonaws\.com\/com\.urbanairship\.media\/.*/i,
  ]

  const sentryConfig: Record<string, unknown> = {
    dsn,
    allowUrls,
    environment,
    release,
    normalizeDepth: 4,
    sendDefaultPii,
  }

  const integrations = [Sentry.rewriteFramesIntegration({root: rootDir})]

  // Make sure tracing config is only included if the rate is > 0
  // see https://docs.sentry.io/platforms/javascript/tracing/#disabling-tracing
  if (tracesSampleRate > 0) {
    integrations.push(Sentry.browserTracingIntegration())
    sentryConfig.tracesSampleRate = tracesSampleRate
  }

  sentryConfig.integrations = integrations

  Sentry.init(sentryConfig)

  // If an event has an Error, add the Error's properties as event context.
  // Useful for custom error types that define their own properties.
  Sentry.addEventProcessor((event, hint) => {
    if (hint.originalException instanceof Error) {
      event.contexts = {
        ...event.contexts,
        errorProperties: {...hint.originalException},
      }
    }
    return event
  })

  Sentry.setTags(stringifyValues(sentryData.TAGS))

  if (sentryData.USER) {
    Sentry.setUser(stringifyValues(sentryData.USER))
  }
}

function stringifyValues(obj: {[key: string]: number | boolean | string}): {
  [key: string]: string
} {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, value.toString()])
  )
}
